import { useRef, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, InputBase, SxProps, Theme, Typography } from '@mui/material';

import { stickyNoteSurfaceSx } from './StickyNoteCard';
import { STICKY_NOTE_MAX_LENGTH, STICKY_NOTE_STRIP_HEIGHT } from './stickyNotes.constants';

const STRINGS = appStrings.modules.priorities.stickyNotes;

type StickyNoteStarterProps = Readonly<{
  onChange: (text: string) => void;
  hasError: boolean;
}>;

/** An unsaved note shown in place of an empty board; nothing persists until text is entered. */
export const StickyNoteStarter = ({ onChange, hasError }: StickyNoteStarterProps) => {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  return (
    <Box id="sticky-note-starter" onClick={() => inputRef.current?.focus()} sx={starterSx}>
      <InputBase
        id="sticky-note-starter-input"
        inputRef={inputRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value);
        }}
        inputProps={{ maxLength: STICKY_NOTE_MAX_LENGTH, 'aria-label': STRINGS.editAria }}
        placeholder={STRINGS.starterPlaceholder}
        multiline
        fullWidth
        sx={inputSx}
      />
      {hasError && (
        <Typography variant="text-xs-regular" aria-live="polite" sx={errorSx}>
          {STRINGS.saveError}
        </Typography>
      )}
    </Box>
  );
};

const starterSx: SxProps<Theme> = (theme) => ({
  ...stickyNoteSurfaceSx(theme, 'color_1'),
  position: 'relative',
  width: '100%',
  pt: `${STICKY_NOTE_STRIP_HEIGHT}px`,
  px: theme.backrs.spacing['spacing-xl'],
  pb: theme.backrs.spacing['spacing-lg'],
  overflowY: 'auto',
  cursor: 'text',
});

const inputSx: SxProps<Theme> = ({ backrs, typography }) => ({
  alignItems: 'flex-start',
  p: 0,
  ...typography['text-md-regular'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'block',
  mt: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-error-primary'],
});
